import { Inject, Injectable, Injector, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { UserService } from '@hub-services/user.service';
import {
  MSAL_GUARD_CONFIG,
  MsalBroadcastService,
  MsalGuardConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  AccountInfo,
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionRequiredAuthError,
  InteractionStatus,
  RedirectRequest,
} from '@azure/msal-browser';
import { environment } from '@hub-env/environment';
import { FaroObservabilityService } from '@hub-services/faro-observability.service';
import { UserflowService } from '@hub-services/userflow.service';
import { BehaviorSubject, defer, EMPTY, from, Observable, ObservableInput, Subject, throwError, timer } from 'rxjs';
import { catchError, filter, map, retry, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { classifyAuthError } from './auth-error-classifier';
import { AuthRedirectInitiatedError } from './auth-redirect-initiated-error';

/** Storage key MSAL writes when a redirect starts; cleared by handleRedirectPromise on return. */
const MSAL_INTERACTION_STATUS_KEY = 'msal.interaction.status';
/** Our own timestamp, set alongside each redirect, used to age out stale interaction.status entries. */
const REDIRECT_START_TIMESTAMP_KEY = 'hub.msal.redirect.start';
/** How long a "redirect in progress" can plausibly take before we treat the status as stale. */
const STALE_INTERACTION_THRESHOLD_MS = 60_000;

/** One short retry absorbs a transient token-endpoint failure before forcing re-auth. */
export const TOKEN_ACQUIRE_RETRY_COUNT = 1;
export const TOKEN_ACQUIRE_RETRY_DELAY_MS = 500;

/**
 * Cross-redirect re-auth loop breaker: stop after REAUTH_LOOP_MAX interactive
 * redirects within REAUTH_LOOP_WINDOW_MS and land on access-denied instead of
 * bouncing the user forever. Persisted in sessionStorage so it survives the
 * B2C round-trip (the in-memory reauthInFlight latch only covers one page life).
 */
export const REAUTH_ATTEMPTS_KEY = 'hub.msal.reauth.attempts';
export const REAUTH_LOOP_MAX = 3;
const REAUTH_LOOP_WINDOW_MS = 60_000;

/**
 * Singleton owner of MSAL event handling and auth-flow side effects.
 *
 * Initialized via APP_INITIALIZER chained after MSAL's own `instance.initialize()`
 * resolves — see `initializeMsalApp` in `msal-config.ts`. Subscriptions wired in
 * `initialize()` are the source of truth for auth state; components and guards
 * should subscribe to `accounts$` rather than query MSAL directly.
 */
@Injectable({ providedIn: 'root' })
export class AuthService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private initialized = false;
  private interactionInProgress = false;
  // Latched once an interactive redirect is kicked off so concurrent token
  // failures trigger exactly one redirect. Never reset: the redirect unloads
  // the page and destroys this singleton.
  private reauthInFlight = false;
  // Set once we've routed the user to /access-denied so SessionResolve doesn't
  // redundantly route there again. Never reset (access-denied is terminal).
  private accessDeniedShown = false;
  private readonly accountsSubject$ = new BehaviorSubject<AccountInfo[]>([]);

  readonly accounts$: Observable<AccountInfo[]> = this.accountsSubject$.asObservable();

  constructor(
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private router: Router,
    private userflowService: UserflowService,
    private faro: FaroObservabilityService,
    private injector: Injector,
  ) { }

  /** Idempotent — subsequent calls are no-ops. */
  initialize(redirectResult?: AuthenticationResult | null): void {
    if (this.initialized) return;
    this.initialized = true;

    // Self-heal stale interaction state before any subsequent loginRedirect /
    // acquireTokenRedirect call can be refused with `interaction_in_progress`.
    // Runs only after MSAL is fully initialized (see initializeMsalApp in
    // msal-config.ts), which guarantees handleRedirectPromise has had a chance
    // to consume legitimate in-flight state first.
    this.attemptWedgeRecovery();

    // Without this, ACCOUNT_ADDED / ACCOUNT_REMOVED fire only for in-tab events.
    this.msalService.instance.enableAccountStorageEvents();

    // Tracked so `handleReauth` can suppress duplicate silent-failure signals
    // that fire while an interactive redirect is already in flight.
    this.msalBroadcastService.inProgress$
      .pipe(takeUntil(this.destroy$))
      .subscribe(status => {
        this.interactionInProgress = status !== InteractionStatus.None;
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) =>
          (msg.eventType === EventType.LOGIN_FAILURE
            || msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE)
          && msg.error != null
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(msg => this.handleFailureEvent(msg));

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) =>
          msg.eventType === EventType.LOGIN_SUCCESS
          || msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
          || msg.eventType === EventType.SSO_SILENT_SUCCESS
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(msg => this.handleSuccessEvent(msg));

    // Listen to the lifecycle events in addition to ACCOUNT_ADDED / ACCOUNT_REMOVED
    // because MSAL doesn't always fire the account-change events alongside them.
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) =>
          msg.eventType === EventType.ACCOUNT_ADDED
          || msg.eventType === EventType.ACCOUNT_REMOVED
          || msg.eventType === EventType.LOGIN_SUCCESS
          || msg.eventType === EventType.LOGOUT_SUCCESS
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.refreshAccounts());

    // Cross-tab logout signal. ACCOUNT_REMOVED fires here only when ANOTHER
    // tab cleared MSAL's localStorage (browser storage events don't fire in
    // the writing tab). Deliberately NOT LOGOUT_SUCCESS: that fires in the
    // same tab as logoutRedirect() BEFORE the browser navigates to B2C, so
    // hijacking it would short-circuit the server-side sign-out and the
    // configured postLogoutRedirectUri.
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.ACCOUNT_REMOVED),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        // An account left the cache — drop any user mapped from it so stale
        // roles/org access can't survive into the next read.
        this.injector.get(UserService).clearCachedUser();
        if (this.msalService.instance.getAllAccounts().length === 0) {
          window.location.pathname = '/';
        }
      });

    // Seed runs last so the initial emission reflects any setActiveAccount
    // call inside refreshAccounts().
    this.refreshAccounts();

    // Count only completed sign-up/sign-in policy redirects, not token refresh
    // redirects or other B2C policy returns.
    if (this.isSignUpSignInResult(redirectResult)) {
      this.injector.get(UserService).recordSignIn().pipe(take(1)).subscribe();
    }
  }

  /**
   * Wedge self-heal. If `msal.interaction.status` is set in storage but
   * `inProgress$` reports `None`, MSAL believes an interaction is in flight
   * that isn't actually happening — the next `loginRedirect()` will be
   * refused with `interaction_in_progress` and the user is stuck.
   *
   * Cross-tab guard: another tab on the same origin may have legitimately
   * started a redirect <60s ago (same `localStorage`). The timestamp
   * `hub.msal.redirect.start`, stamped by every redirect we initiate, lets us
   * tell "abandoned long ago" (clear) from "in-flight elsewhere" (leave alone).
   * Missing timestamp is treated as infinitely old — anything written by a
   * previous app version or by MSAL itself with no companion stamp is fair
   * game to clear.
   */
  private attemptWedgeRecovery(): void {
    this.msalBroadcastService.inProgress$
      .pipe(take(1))
      .subscribe(status => {
        if (status !== InteractionStatus.None) return;
        this.clearStaleInteractionState();
      });
  }

  private clearStaleInteractionState(): void {
    const sessionStatus = this.safeStorageGet(sessionStorage, MSAL_INTERACTION_STATUS_KEY);
    const localStatus = this.safeStorageGet(localStorage, MSAL_INTERACTION_STATUS_KEY);
    if (!sessionStatus && !localStatus) return;

    const startedAtRaw = this.safeStorageGet(sessionStorage, REDIRECT_START_TIMESTAMP_KEY)
                      ?? this.safeStorageGet(localStorage, REDIRECT_START_TIMESTAMP_KEY);
    const startedAt = startedAtRaw ? parseInt(startedAtRaw, 10) : 0;
    const ageMs = startedAt ? Date.now() - startedAt : Number.POSITIVE_INFINITY;
    if (ageMs < STALE_INTERACTION_THRESHOLD_MS) return;

    console.warn('[AuthService] Clearing stale msal.interaction.status (wedge recovery)', {
      sessionStatus,
      localStatus,
      startedAt: startedAt || null,
      ageMs: Number.isFinite(ageMs) ? ageMs : null,
    });

    this.safeStorageRemove(sessionStorage, MSAL_INTERACTION_STATUS_KEY);
    this.safeStorageRemove(localStorage, MSAL_INTERACTION_STATUS_KEY);
    this.safeStorageRemove(sessionStorage, REDIRECT_START_TIMESTAMP_KEY);
    this.safeStorageRemove(localStorage, REDIRECT_START_TIMESTAMP_KEY);

    // Flow-specific keys that MSAL leaves behind alongside an abandoned
    // interaction. Clearing them isn't strictly necessary — MSAL would
    // overwrite on the next redirect — but it keeps storage tidy and matches
    // what manual recovery (DevTools → Application → Clear site data) does.
    const clientId = environment.msalConfig.auth.clientId;
    for (const key of [
      `msal.${clientId}.request.params`,
      `msal.${clientId}.code.verifier`,
      `msal.${clientId}.request.origin`,
    ]) {
      this.safeStorageRemove(sessionStorage, key);
      this.safeStorageRemove(localStorage, key);
    }
  }

  /** Stamp a timestamp alongside each MSAL redirect we initiate, so the next page-load self-heal can age it out. */
  private stampRedirectStart(): void {
    this.safeStorageSet(sessionStorage, REDIRECT_START_TIMESTAMP_KEY, Date.now().toString());
  }

  private safeStorageGet(storage: Storage, key: string): string | null {
    try { return storage.getItem(key); } catch { return null; }
  }
  private safeStorageSet(storage: Storage, key: string, value: string): void {
    try { storage.setItem(key, value); } catch { /* storage unavailable; non-fatal */ }
  }
  private safeStorageRemove(storage: Storage, key: string): void {
    try { storage.removeItem(key); } catch { /* storage unavailable; non-fatal */ }
  }

  /** Idempotent — subscribers can dedupe via distinctUntilChanged if needed. */
  private refreshAccounts(): void {
    const accounts = this.msalService.instance.getAllAccounts();
    if (accounts.length > 0 && !this.msalService.instance.getActiveAccount()) {
      this.msalService.instance.setActiveAccount(accounts[0]);
    }
    this.accountsSubject$.next(accounts);
  }

  /**
   * Set active account on signUpSignIn policy success only. Other B2C policies
   * (password-reset, profile-edit) fire the same success events but shouldn't
   * override the primary active account.
   */
  private handleSuccessEvent(msg: EventMessage): void {
    const payload = msg.payload as AuthenticationResult | null;
    if (this.isSignUpSignInResult(payload)) {
      this.msalService.instance.setActiveAccount(payload!.account);
    }
  }

  private isSignUpSignInResult(payload?: AuthenticationResult | null): boolean {
    const claims = (payload?.idTokenClaims as { acr?: string; tfp?: string } | undefined) ?? {};
    const policy = environment.b2cPolicies.names.signUpSignIn;
    return claims.acr?.toLowerCase() === policy || claims.tfp?.toLowerCase() === policy;
  }

  /** Classifier owns "what happened"; this switch owns "what to do." */
  private handleFailureEvent(msg: EventMessage): void {
    const outcome = classifyAuthError(msg.error);
    console.warn(`[AuthService] ${msg.eventType} -> ${outcome.kind}`, {
      reason: outcome.reason,
      errorCode: outcome.errorCode,
    });

    // Route MSAL broadcast failures through the same central policy as
    // acquireApiTokenOrReauth so they share the loop guard, in-flight gate, and
    // access-denied + Faro logging instead of being a parallel pipeline.
    const detail: Record<string, string> = {
      source: msg.eventType,
      'auth.error_code': outcome.errorCode ?? outcome.reason,
    };
    switch (outcome.kind) {
      case 'reauth':
        this.triggerReauth('login', detail);
        break;
      case 'logout_then_reauth':
        this.triggerReauth('logout', detail);
        break;
      case 'denied':
        this.goToAccessDenied('idp_denied', detail);
        break;
      case 'config_error':
        this.goToAccessDenied('auth_config_error', detail);
        break;
      case 'cancelled':
        // Dormant under redirect-only flows.
        break;
    }
  }

  /**
   * Single canonical logout.
   *
   * - `idTokenHint` disambiguates which account to log out when multiple are
   *   cached; without it B2C prompts the user "which account?".
   * - `userflowService.reset()` lives here (not at callers) so the internal
   *   caller via `handleFailureEvent('logout_then_reauth')` gets it too.
   *   Caller-side `reset()` in header / avatar / terms is now redundant but
   *   harmless (Userflow's reset is idempotent).
   */
  logout(): void {
    this.userflowService.reset();
    this.injector.get(UserService).clearCachedUser();
    const account = this.msalService.instance.getActiveAccount()
                 ?? this.msalService.instance.getAllAccounts()[0];
    this.stampRedirectStart();
    this.msalService.logoutRedirect({
      postLogoutRedirectUri: environment.postLogoutRedirectUri,
      idTokenHint: account?.idToken,
    }).subscribe({
      error: (err) => {
        console.error('[AuthService] logoutRedirect error:', err);
        this.reauthInFlight = false;
      },
    });
  }

  /**
   * Trigger an interactive login. By default uses `MSAL_GUARD_CONFIG.authRequest`
   * (API scopes from the guard config). Pass `override` to merge additional /
   * replacement params — used by post-registration flows that need
   * `prompt: 'login'`, a `login_hint`, etc.
   */
  loginRedirect(override?: Partial<RedirectRequest>): void {
    const base = this.msalGuardConfig.authRequest as RedirectRequest | undefined;
    const request: RedirectRequest | undefined =
      base || override ? { ...(base ?? {}), ...(override ?? {}) } as RedirectRequest : undefined;
    this.stampRedirectStart();
    const call = request ? this.msalService.loginRedirect(request) : this.msalService.loginRedirect();
    call.subscribe({
      error: (err) => {
        console.error('[AuthService] loginRedirect error:', err);
        this.reauthInFlight = false;
      },
    });
  }

  /** Synchronous snapshot of whether MSAL has any cached accounts. */
  hasAccounts(): boolean {
    return this.msalService.instance.getAllAccounts().length > 0;
  }

  /** Synchronous snapshot of the current active account, or null. */
  getActiveAccount(): AccountInfo | null {
    return this.msalService.instance.getActiveAccount();
  }

  /**
   * Silent acquisition of an API-scoped access token from MSAL's cache.
   *
   * Errors are surfaced as-is — callers decide what to do (interceptor passes
   * the request through without a token; setUser triggers a redirect; etc.).
   * `InteractionRequiredAuthError` is the typical signal that the caller should
   * fall back to an interactive flow via `startAuthRedirect()` or `loginRedirect()`.
   */
  /** Active account, falling back to the first cached account (the account acquireApiToken would use). */
  getActiveOrFirstAccount(): AccountInfo | null {
    return this.msalService.instance.getActiveAccount()
        ?? this.msalService.instance.getAllAccounts()[0]
        ?? null;
  }

  acquireApiToken(): Observable<AuthenticationResult> {
    const account = this.getActiveOrFirstAccount();
    if (!account) {
      return throwError(() => new Error('[AuthService] acquireApiToken: no cached account'));
    }
    return from(this.msalService.instance.acquireTokenSilent({
      account,
      scopes: environment.apiConfig.scopes,
      redirectUri: environment.silentRedirectUri,
    }));
  }

  /**
   * Single source of truth for "get a usable API token, or send the user back
   * through auth." Applies the shared policy so callers (interceptor, user/role
   * resolution) never re-implement it:
   *  - transient failure        -> one short retry
   *  - reauth / logout_then_reauth -> loop-guarded interactive redirect, then
   *                                throw AuthRedirectInitiatedError
   *  - denied / config          -> propagate the original error (caller decides)
   *
   * Callers therefore see exactly one of: a token, the redirect sentinel, or a
   * positive denial — never a tokenless success.
   */
  acquireApiTokenOrReauth(): Observable<AuthenticationResult> {
    // defer so retry re-invokes acquireApiToken; the underlying promise would
    // otherwise just replay its first (failed) result.
    return defer(() => this.acquireApiToken()).pipe(
      retry({
        count: TOKEN_ACQUIRE_RETRY_COUNT,
        delay: (error) => this.retryAcquire(error),
      }),
      tap(() => this.clearReauthAttempts()),
      catchError((error) => this.onAcquireFailure(error)),
    );
  }

  private retryAcquire(error: unknown): ObservableInput<number> {
    // Retry only transient failures; deterministic states gain nothing from a
    // wait, so abort and let onAcquireFailure handle them. No cached account is
    // deterministic (retrying can't conjure one) — skip straight to re-auth.
    if (
      error instanceof InteractionRequiredAuthError ||
      !this.hasAccounts() ||
      classifyAuthError(error).kind !== 'reauth'
    ) {
      return throwError(() => error);
    }
    return timer(TOKEN_ACQUIRE_RETRY_DELAY_MS);
  }

  private onAcquireFailure(error: unknown): Observable<never> {
    const outcome = classifyAuthError(error);
    switch (outcome.kind) {
      case 'reauth':
        // No account -> full login; account present but token unusable -> token redirect.
        this.triggerReauth(this.hasAccounts() ? 'token' : 'login');
        return throwError(() => new AuthRedirectInitiatedError());
      case 'logout_then_reauth':
        this.triggerReauth('logout');
        return throwError(() => new AuthRedirectInitiatedError());
      case 'config_error':
        // Re-login can't fix a misconfigured client (it would loop), so land on
        // access-denied with the reason logged for operators.
        return this.denyAccess('auth_config_error', { 'auth.error_code': outcome.errorCode ?? outcome.reason });
      case 'denied':
        return this.denyAccess('idp_denied', { 'auth.error_code': outcome.errorCode ?? outcome.reason });
      default:
        // cancelled / unknown — nothing actionable; propagate.
        return throwError(() => error);
    }
  }

  /**
   * Loop-guarded interactive re-auth. All "credentials unusable" paths funnel
   * here so concurrent failures cause exactly one redirect.
   * - 'login'  : no account to acquire against -> full login.
   * - 'token'  : account present, token unusable -> interactive token redirect.
   * - 'logout' : local/B2C state is stuck -> clear via logout first.
   */
  triggerReauth(via: 'login' | 'token' | 'logout' = 'token', detail: Record<string, string> = {}): void {
    if (this.reauthInFlight) {
      return;
    }
    // Interactive re-auth is skipped while MSAL already has an interaction in
    // flight (avoids a double redirect); logout must run regardless to clear
    // stuck local/B2C state.
    if (via !== 'logout' && this.interactionInProgress) {
      return;
    }
    this.reauthInFlight = true;

    if (this.reauthLoopDetected()) {
      // Too many redirect cycles in the window — stop looping and land on
      // access-denied so the user isn't bounced indefinitely.
      this.goToAccessDenied('reauth_loop', { 'reauth.via': via, ...detail });
      return;
    }

    this.faro.pushEvent('reauth_redirect', { 'reauth.via': via, ...detail });
    switch (via) {
      case 'login':
        this.loginRedirect();
        break;
      case 'logout':
        this.logout();
        break;
      default:
        this.startAuthRedirect();
    }
  }

  /**
   * Classify a backend 401 against MSAL and act on it. Unlike a token-acquisition
   * failure, here MSAL handed us a token the server then refused, so we re-probe
   * MSAL to tell the two recoverable shapes apart:
   *  - MSAL can still mint a token: the server rejected a valid token, so
   *    re-login won't help -> /access-denied.
   *  - MSAL can no longer mint a token: credentials are stale -> loop-guarded
   *    re-auth (acquireApiTokenOrReauth's own failure path).
   * Always errors; the originating request is terminal either way.
   */
  handleApiAuthRejection(status: number): Observable<never> {
    // A backend 401 means the cached user (roles/identity) may be stale — drop
    // it so the next read re-derives from a fresh token.
    this.injector.get(UserService).clearCachedUser();
    return this.acquireApiTokenOrReauth().pipe(
      // MSAL still mints a token: the server rejected a valid one, so re-login
      // would only reproduce it. (If MSAL can't mint one, acquireApiTokenOrReauth
      // already triggered re-auth and this errors before the switchMap.)
      switchMap(() => this.denyAccess('api_token_rejected', { 'http.status_code': String(status) })),
    );
  }

  /**
   * Called by SessionResolve when /api/session yields no usable data. If the
   * auth layer already routed the user (re-auth redirect or access-denied),
   * stay out of its way; otherwise treat it as a genuine session failure and
   * route to access-denied with a reason rather than cancelling into a blank
   * startup state.
   */
  handleSessionUnavailable(): Observable<never> {
    if (this.reauthInFlight || this.accessDeniedShown) {
      return EMPTY;
    }
    this.goToAccessDenied('session_unavailable');
    return EMPTY;
  }

  private denyAccess(reason: string, detail: Record<string, string> = {}): Observable<never> {
    this.goToAccessDenied(reason, detail);
    return throwError(() => new Error(`[AuthService] access denied: ${reason}`));
  }

  /**
   * Route to /access-denied. The page itself stays generic; the explicit reason
   * the user landed here (token rejected, config error, IdP denial, re-auth
   * loop) is logged to Faro/Grafana for triage rather than shown in the UI.
   */
  private goToAccessDenied(reason: string, detail: Record<string, string> = {}): void {
    this.accessDeniedShown = true;
    this.faro.pushEvent('access_denied', { reason, ...detail });
    this.router.navigate(['/access-denied']);
  }

  /**
   * Cross-redirect circuit breaker. reauthInFlight only dedupes within one page
   * life; this windowed counter survives the B2C round-trip, so a login that
   * keeps returning unusable credentials trips after REAUTH_LOOP_MAX attempts.
   * Self-clears on trip and on the next successful acquisition.
   */
  private reauthLoopDetected(): boolean {
    const now = Date.now();
    let count = 0;
    let firstAt = now;
    const raw = this.safeStorageGet(sessionStorage, REAUTH_ATTEMPTS_KEY);
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as { count?: number; firstAt?: number };
        if (typeof parsed.firstAt === 'number' && now - parsed.firstAt < REAUTH_LOOP_WINDOW_MS) {
          count = parsed.count ?? 0;
          firstAt = parsed.firstAt;
        }
      } catch { /* malformed — treat as a fresh window */ }
    }
    count += 1;
    if (count > REAUTH_LOOP_MAX) {
      this.safeStorageRemove(sessionStorage, REAUTH_ATTEMPTS_KEY);
      console.warn('[AuthService] re-auth loop detected; routing to access-denied', { count });
      return true;
    }
    this.safeStorageSet(sessionStorage, REAUTH_ATTEMPTS_KEY, JSON.stringify({ count, firstAt }));
    return false;
  }

  private clearReauthAttempts(): void {
    this.safeStorageRemove(sessionStorage, REAUTH_ATTEMPTS_KEY);
  }

  waitForIdle(): Observable<void> {
    return this.msalBroadcastService.inProgress$.pipe(
      filter((status: InteractionStatus) => status === InteractionStatus.None),
      take(1),
      map(() => void 0)
    );
  }

  startAuthRedirect(): void {
    this.stampRedirectStart();
    this.msalService.instance.acquireTokenRedirect({
      scopes: environment.apiConfig.scopes,
    }).catch((err) => {
      // Redirect never navigated (network blip, blocked nav, interaction race):
      // release the latch so the next attempt isn't permanently wedged.
      console.error('[AuthService] acquireTokenRedirect error:', err);
      this.reauthInFlight = false;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
